<template>
  <Modal ref="modal">
    <h2 class="box__title">{{ $t('createDocumentModal.title') }}</h2>
    <Error :error="error"></Error>
    <form @submit.prevent="submit">
      <FormGroup small-label required class="margin-bottom-2">
        <template #label>
          <i class="iconoir-text"></i>
          {{ $t('createDocumentModal.nameLabel') }}
        </template>
        <FormInput
          ref="nameInput"
          v-model="name"
          size="large"
          :placeholder="$t('docsHub.untitled')"
        ></FormInput>
      </FormGroup>
      <FormGroup small-label required class="margin-bottom-2">
        <template #label>
          <i class="iconoir-page"></i>
          {{ $t('createDocumentModal.typeLabel') }}
        </template>
        <Dropdown v-model="type" size="large" :show-search="false">
          <DropdownItem
            :name="$t('docsHub.blankDocument')"
            value=""
            icon="iconoir-page"
          ></DropdownItem>
          <DropdownItem
            v-for="template in docTemplates"
            :key="template.key"
            :name="template.name"
            :value="template.key"
            :icon="template.icon"
          ></DropdownItem>
          <DropdownItem
            :name="$t('docsHub.newWhiteboard')"
            value="whiteboard"
            icon="iconoir-design-pencil"
          ></DropdownItem>
        </Dropdown>
      </FormGroup>
      <div class="actions">
        <div class="align-right">
          <Button
            type="primary"
            size="large"
            :loading="loading"
            :disabled="loading"
          >
            {{ $t('createDocumentModal.create') }}
          </Button>
        </div>
      </div>
    </form>
  </Modal>
</template>

<script>
import modal from '@baserow/modules/core/mixins/modal'
import error from '@baserow/modules/core/mixins/error'
import DocsService from '@baserow/modules/core/services/docs'
import WhiteboardsService from '@baserow/modules/core/services/whiteboards'
import { DOC_TEMPLATES } from '@baserow/modules/core/docTemplates'

/**
 * Origem Flow: modal de criação de documento com escolha do tipo — em branco,
 * um dos modelos prontos ou um quadro branco. Aberto pelo botão direito na
 * pasta "Documentos" do sidebar.
 */
export default {
  name: 'CreateDocumentModal',
  mixins: [modal, error],
  props: {
    workspaceId: {
      type: Number,
      required: true,
    },
  },
  emits: ['created'],
  data() {
    return {
      name: '',
      type: '',
      loading: false,
      docTemplates: DOC_TEMPLATES,
    }
  },
  methods: {
    show(...args) {
      this.name = ''
      this.type = ''
      this.hideError()
      modal.methods.show.call(this, ...args)
      this.$nextTick(() => {
        this.$refs.nameInput?.focus()
      })
    },
    async submit() {
      if (this.loading) {
        return
      }
      this.loading = true
      this.hideError()
      try {
        if (this.type === 'whiteboard') {
          const { data } = await WhiteboardsService(this.$client).create(
            this.workspaceId,
            { name: this.name }
          )
          this.$emit('created', { type: 'whiteboard', whiteboard: data })
          this.hide()
          this.$router.push({
            name: 'workspace-whiteboards',
            params: { workspaceId: this.workspaceId },
            query: { board: data.id },
          })
        } else {
          const template =
            this.docTemplates.find((t) => t.key === this.type) || null
          const { data } = await DocsService(this.$client).create(
            this.workspaceId,
            {
              // Sem nome digitado, um doc de modelo herda o nome do modelo.
              name: this.name || (template ? template.name : ''),
              content: template ? template.content : null,
              docType: this.type,
            }
          )
          this.$emit('created', { type: 'document', document: data })
          this.hide()
          this.$router.push({
            name: 'workspace-docs',
            params: { workspaceId: this.workspaceId },
            query: { doc: data.id },
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
